// ---------------------------------------------------------------------------
// DnD Email Editor V2 – Toolbar
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import {
  Undo2,
  Redo2,
  History,
  LayoutTemplate,
  Monitor,
  Smartphone,
  FileText,
  Eye,
  Code,
  Maximize2,
  Minimize2,
  Save,
  ChevronDown,
  GitBranch,
} from 'lucide-react';

interface DndToolbarProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  canRevert: boolean;
  onRevert: () => void;
  /** Omitted when this editor is not editing a whole email (e.g. marketing settings header/footer). */
  onOpenTemplatePicker?: () => void;
  device: 'desktop' | 'mobile' | 'plaintext';
  onDeviceChange: (device: 'desktop' | 'mobile' | 'plaintext') => void;
  onPreview: () => void;
  onSave: () => void;
  /**
   * Turns Save into a menu offering "Save as New Version" alongside it, matching
   * the HTML editor's fullscreen bar. Omitted where versioning has no meaning
   * (a new unsaved template, or the marketing-settings header/footer editor),
   * and Save then stays a plain button.
   */
  onSaveAsNewVersion?: () => void;
  onConvertToHtml?: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  isDirty: boolean;
  /** Hide the built-in Save button when the host page provides its own Save. */
  hideSave?: boolean;
}

export function DndToolbar({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  canRevert,
  onRevert,
  onOpenTemplatePicker,
  device,
  onDeviceChange,
  onPreview,
  onSave,
  onSaveAsNewVersion,
  onConvertToHtml,
  isFullscreen,
  onToggleFullscreen,
  isDirty,
  hideSave = false,
}: DndToolbarProps) {
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const saveMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSaveMenu) return;
    const handler = (e: MouseEvent) => {
      if (saveMenuRef.current && !saveMenuRef.current.contains(e.target as Node)) {
        setShowSaveMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSaveMenu]);

  return (
    <div className="dnd-toolbar">
      {/* Left group: Undo, Redo, Revert */}
      <div className="dnd-toolbar-group">
        <button className="dnd-toolbar-btn" onClick={onUndo} disabled={!canUndo} title="Undo (Ctrl+Z)">
          <Undo2 size={14} />
        </button>
        <button className="dnd-toolbar-btn" onClick={onRedo} disabled={!canRedo} title="Redo (Ctrl+Y)">
          <Redo2 size={14} />
        </button>
        <button className="dnd-toolbar-btn" onClick={onRevert} disabled={!canRevert} title="Revert to saved">
          <History size={14} />
        </button>
      </div>

      <div className="dnd-toolbar-spacer" />

      {/* Center group: Device toggles (centered between the two spacers) */}
      <div className="dnd-toolbar-group">
        <button className={`dnd-toolbar-btn${device === 'desktop' ? ' active' : ''}`} onClick={() => onDeviceChange('desktop')} title="Desktop preview">
          <Monitor size={14} />
        </button>
        <button className={`dnd-toolbar-btn${device === 'mobile' ? ' active' : ''}`} onClick={() => onDeviceChange('mobile')} title="Mobile preview">
          <Smartphone size={14} />
        </button>
        <button className={`dnd-toolbar-btn${device === 'plaintext' ? ' active' : ''}`} onClick={() => onDeviceChange('plaintext')} title="Plain text">
          <FileText size={14} />
        </button>
      </div>

      <div className="dnd-toolbar-spacer" />

      {/* Unsaved changes indicator (mirrors the HTML editor's fullscreen indicator) */}
      {isDirty && (
        <div className="dnd-unsaved-indicator" title="You have unsaved changes">
          <span className="dnd-unsaved-dot" />
          <span>Unsaved changes</span>
        </div>
      )}

      {/* Right group: Convert, Preview, Save, Fullscreen */}
      {onConvertToHtml && (
        <div className="dnd-toolbar-group" style={{ position: 'relative' }}>
          <button className="dnd-toolbar-btn" onClick={() => setShowConvertConfirm(true)} title="Convert to HTML editor (one-way)">
            <Code size={14} />
            <span className="dnd-toolbar-label">Convert to HTML</span>
          </button>
          {showConvertConfirm && (
            <div className="dnd-convert-confirm">
              <p>This will convert your design to HTML. You will not be able to switch back to the drag & drop editor. Continue?</p>
              <div className="dnd-convert-confirm-actions">
                <button onClick={() => setShowConvertConfirm(false)}>Cancel</button>
                <button className="dnd-btn-danger" onClick={() => { setShowConvertConfirm(false); onConvertToHtml(); }}>Convert</button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="dnd-toolbar-group">
        {onOpenTemplatePicker && (
          <button className="dnd-toolbar-btn" onClick={onOpenTemplatePicker} title="Choose template" aria-label="Templates">
            <LayoutTemplate size={14} />
          </button>
        )}
        <button className="dnd-toolbar-btn" onClick={onPreview} title="Preview email">
          <Eye size={14} />
        </button>
        {!hideSave && (onSaveAsNewVersion ? (
          <div className="dnd-save-menu-wrap" ref={saveMenuRef}>
            <button
              className="dnd-toolbar-btn dnd-btn-primary"
              onClick={() => setShowSaveMenu((v) => !v)}
              title="Save options"
            >
              <Save size={14} />
              <span className="dnd-toolbar-label">{isDirty ? 'Save*' : 'Save'}</span>
              <ChevronDown size={13} />
            </button>
            {showSaveMenu && (
              <div className="dnd-save-menu">
                <button onClick={() => { setShowSaveMenu(false); onSave(); }}>
                  <Save size={13} /> Save
                </button>
                <button onClick={() => { setShowSaveMenu(false); onSaveAsNewVersion(); }}>
                  <GitBranch size={13} /> Save as New Version
                </button>
              </div>
            )}
          </div>
        ) : (
          <button className="dnd-toolbar-btn dnd-btn-primary" onClick={onSave} title="Save (Ctrl+S)">
            <Save size={14} />
            <span className="dnd-toolbar-label">{isDirty ? 'Save*' : 'Save'}</span>
          </button>
        ))}
        {/* Sits after Save so leaving fullscreen is the last thing in the bar,
            next to where the eye naturally ends up after saving. */}
        <button className="dnd-toolbar-btn" onClick={onToggleFullscreen} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
          {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        </button>
      </div>
    </div>
  );
}
